import React, { useEffect } from 'react';
import { useIsPageHidden, useUpdatingRef } from 'shared/hooks';
import { useAppSelector } from 'shared/store';
import {
  isAppNotificationSoundPlayEligible,
  selectAppNotificationsListSoundPlayable,
} from 'shared/store/app-notification-slice';
import { selectRealtimeAssociatedVisitor } from 'shared/store/realtime-slice';
import { selectActiveUser } from 'shared/store/shared-selectors';
import { makeLog } from 'shared/utils/log';
import {
  APP_NOTIFICATION_CONFIG,
  AppNotification,
  AppNotificationType,
  isAppNotificationVisitorCall,
  isAppNotificationVisitorConversationUpdate,
} from 'types';

const log = makeLog('AppNotificationSoundPlayer');

export const AppNotificationSoundPlayer: React.FC = () => {
  useAppNotificationSounds();
  return null;
};

// Manage sound playing
const persistAndCheckPlayedSound = (id: number | string) => {
  const k = 'sb:sound-played-for-app-notification';
  const v = sessionStorage.getItem(k) || '[]';
  const played = new Set(JSON.parse(v));
  const hasPlayed = played.has(id);
  played.add(id);
  sessionStorage.setItem(k, JSON.stringify([...played]));
  return hasPlayed;
};

const playBellDingAudio = (delay?: number) => {
  const audio = new Audio('https://assets.servicebell.com/bell-ding.mp3');
  audio.volume = 0.6;
  delay
    ? setTimeout(() => void audio.play().catch(() => null /* no-op */), delay)
    : void audio.play().catch(() => null /* no-op */);
};

const playChatDingAudio = (delay?: number) => {
  const audio = new Audio('https://assets.servicebell.com/chat-ding-new.mp3');
  audio.volume = 0.6;
  delay
    ? setTimeout(() => void audio.play().catch(() => null /* no-op */), delay)
    : void audio.play().catch(() => null /* no-op */);
};

const useAppNotificationSounds = () => {
  const user = useAppSelector(selectActiveUser);
  const associated = useAppSelector(selectRealtimeAssociatedVisitor);
  const isPageHidden = useIsPageHidden();
  const notifications = useAppSelector(selectAppNotificationsListSoundPlayable);

  // Use refs so our useEffect will only update when notifications change
  // not when page visibility &etc. change
  const userIdRef = useUpdatingRef(user?.id || 0);
  const isAssociatedRef = useUpdatingRef(!!associated);
  const isPageHiddenRef = useUpdatingRef(isPageHidden);

  useEffect(() => {
    if (!userIdRef.current || isAssociatedRef.current) return;
    let wantsBell = null as AppNotification | null;
    let wantsChat = null as AppNotification | null;
    let reason = '';

    notifications.filter(isAppNotificationSoundPlayEligible).forEach(an => {
      if (isAppNotificationVisitorCall(an)) {
        const routedToMe = an.data.routedUserIds.includes(userIdRef.current);
        const emptyRoutedList = !an.data.routedUserIds.length;
        const fresh =
          Date.now() -
            APP_NOTIFICATION_CONFIG[AppNotificationType.visitor_call].popupTimeout <
          Date.parse(an.dateCreated);
        // CALL play sound if routed to me, or not routed to anyone specifically
        if (
          (routedToMe || emptyRoutedList) &&
          fresh &&
          !persistAndCheckPlayedSound(an.id)
        ) {
          wantsBell = an;
          reason = routedToMe ? 'routed to me' : 'not routed to anyone';
        }
      } else if (isAppNotificationVisitorConversationUpdate(an)) {
        // MESSAGE play sound if app-notif not read, visitor unread message count > 0, page hidden and not played sound for message id before
        if (
          !an.dateRead &&
          an.conversation &&
          an.conversation.unreadMessageCounts.visitor > 0 &&
          isPageHiddenRef.current &&
          an.conversation.lastMessage?.id
        ) {
          const playedBefore = persistAndCheckPlayedSound(an.conversation.lastMessage.id);
          if (!playedBefore) {
            wantsChat = an;
            reason =
              'page hidden, notification is unread and conversation has unread visitor message(s) AND not played for last chat message before';
          } else {
            log.debug(
              `Not playing sound for convo update b/c already played for last message`,
            );
          }
        }
      }
    });

    // Only play one sound at a time, even if several were eligible, prefer bell sound
    if (wantsBell) {
      log.debug(
        `Playing bell sound b/c AppNotification[${wantsBell.notificationType}] (${wantsBell.id}), ${reason}`,
      );
      playBellDingAudio();
    } else if (wantsChat) {
      log.debug(
        `Playing chat sound b/c AppNotification[${wantsChat.notificationType}] (${wantsChat.id}), ${reason}`,
      );
      playChatDingAudio();
    }
  }, [notifications, isAssociatedRef, isPageHiddenRef, userIdRef]);
};
